import { Fragment } from 'react'

import type { CustomReportProps } from '..'
import { Card, Empty } from '../shared/Card'
import { count, dataset, text, type Keyed } from '../shared/rows'

import '../report.css'

const GMP_ITEMS = [
  '2 - High',
  '3 - Medium',
  '4 - Low',
  'Score',
  'CARs Opened',
  'CARs Closed',
  'CARs Still Open',
] as const

const SANITATION_ITEMS = [
  'CIP Swabs Collected',
  'CIP Swabs Failed',
  'Listeria Samples Collected',
  'Listeria Samples Failed',
  'Sanitation %',
  'Visual Inspection Completed',
  'Visual Inspection Failed',
] as const

const HC_FILL_WEIGHT_ITEMS = ['10oz', '12oz', 'Gallon', 'Half Gallon', 'Quart'] as const
const TEMPE_FILL_WEIGHT_ITEMS = ['Gallon', 'Half Gallon', 'Quart'] as const
const FILL_WEIGHT_ITEMS = new Set<string>([
  ...HC_FILL_WEIGHT_ITEMS,
  ...TEMPE_FILL_WEIGHT_ITEMS,
])

function formatValue(n: number, item: string): string {
  if (item === 'Score') return n.toFixed(0)
  if (item === 'Sanitation %') {
    const percent = Math.abs(n) <= 1 ? n * 100 : n
    return `${percent.toFixed(2)}%`
  }
  if (FILL_WEIGHT_ITEMS.has(item)) {
    return n.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

function value(row: Keyed | undefined, itemOverride?: string): string {
  if (row === undefined) return formatValue(0, itemOverride ?? '')
  const n = count(row.value)
  return formatValue(n, itemOverride ?? text(row.item) ?? '')
}

function ytdValue(rows: Keyed[], control: string, item: string): string {
  const values = rows
    .filter((row) => text(row.control_name) === control && text(row.item) === item)
    .map((row) => count(row.value))

  if (item === 'Sanitation %') {
    const nonZero = values.filter((n) => n !== 0)
    if (nonZero.length === 0) return '0.00%'
    return formatValue(
      nonZero.reduce((total, n) => total + n, 0) / nonZero.length,
      item,
    )
  }

  return formatValue(
    values.reduce((total, n) => total + n, 0),
    item,
  )
}

function monthHeading(row: Keyed): string {
  return text(row.month_label) || ''
}

function controlGroups(rows: Keyed[]): string[] {
  return Array.from(new Set(rows.map((row) => text(row.control_name) ?? '').filter(Boolean))).sort()
}

function monthsFor(rows: Keyed[]): Array<[string, string]> {
  return Array.from(
    new Map(
      rows
        .slice()
        .sort((a, b) => String(a.month_date).localeCompare(String(b.month_date)))
        .map((row) => [text(row.month_label) ?? '', monthHeading(row)]),
    ).entries(),
  ).filter(([key]) => key !== '')
}

function Matrix({
  rows,
  items,
  firstColumn,
  emptyLabel,
  showYtd = false,
}: {
  readonly rows: Keyed[]
  readonly items: readonly string[]
  readonly firstColumn: string
  readonly emptyLabel: string
  readonly showYtd?: boolean
}) {
  const months = monthsFor(rows)
  const controls = controlGroups(rows)
  const byKey = new Map(
    rows.map((row) => [
      `${text(row.control_name) ?? ''}|${text(row.item) ?? ''}|${text(row.month_label) ?? ''}`,
      row,
    ]),
  )

  if (rows.length === 0 || months.length === 0) {
    return <Empty label={emptyLabel} />
  }

  return (
    <div className="gmp-matrix-scroll">
      <table className="gmp-matrix">
        <thead>
          <tr>
            <th className="gmp-zone-head">{firstColumn}</th>
            {months.map(([key, label]) => (
              <th key={key}>{label}</th>
            ))}
            {showYtd ? <th>YTD</th> : null}
          </tr>
        </thead>
        <tbody>
          {controls.map((control) => (
            <Fragment key={control}>
              <tr className="gmp-control-row" key={`${control}-heading`}>
                <td>{control}</td>
                {months.map(([key]) => (
                  <td key={key}>0</td>
                ))}
                {showYtd ? <td>0</td> : null}
              </tr>
              {items.map((item) => (
                <tr key={`${control}-${item}`}>
                  <td className="gmp-item-cell">{item}</td>
                  {months.map(([key]) => (
                    <td key={key}>{value(byKey.get(`${control}|${item}|${key}`), item)}</td>
                  ))}
                  {showYtd ? <td>{ytdValue(rows, control, item)}</td> : null}
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function QualityDashboard({ result }: CustomReportProps) {
  const rows = dataset(result, 'primary')
  const sanitation = dataset(result, 'sanitation')
  const fillWeight = dataset(result, 'fill_weight')
  const selection = dataset(result, 'selection')[0]
  const plant = text(selection?.plant) ?? ''
  const year = text(selection?.year) ?? ''
  const months = monthsFor(rows)
  const sanitationPlant = text(sanitation[0]?.plant) ?? plant
  const fillWeightItems = plant === 'HC' ? HC_FILL_WEIGHT_ITEMS : TEMPE_FILL_WEIGHT_ITEMS

  return (
    <div className="bespoke-report">
      <p className="report-summary">
        {plant} GMP summary{year === '' ? '' : ` for ${year}`} - {controlGroups(rows).length}{' '}
        GMP zones - {months.length} months
      </p>

      <Card
        title={`GMP - ${plant}`}
        subtitle="Rows follow the Power BI GMP item order: 2 - High, 3 - Medium, 4 - Low, Score, CARs Opened, CARs Closed and CARs Still Open."
      >
        <Matrix
          rows={rows}
          items={GMP_ITEMS}
          firstColumn="GMP Zone"
          emptyLabel="No GMP summary rows for this selection."
        />
      </Card>
      <Card
        title="Food Safety - Sanitation"
        subtitle={`${sanitationPlant} sanitation passed on first time, shown below the GMP matrix.`}
      >
        <Matrix
          rows={sanitation}
          items={SANITATION_ITEMS}
          firstColumn="Category"
          emptyLabel={`No ${plant} sanitation rows for this selection.`}
          showYtd
        />
      </Card>
      <Card
        title="Quality - Fill Weight Averages"
        subtitle={`${plant} monthly bottle fill averages.`}
      >
        <Matrix
          rows={fillWeight}
          items={fillWeightItems}
          firstColumn="Category"
          emptyLabel={`No ${plant} fill weight rows for this selection.`}
          showYtd
        />
      </Card>
    </div>
  )
}
